// src/pages/Documents.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, getDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';
import { logDataAccess } from '../lib/audit/auditLogger';

interface TaskAttachment {
  id: string;
  url: string;
  fileName: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: any;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  clientId: string;
  assignedBy?: string;
  assignedByName?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  status?: 'not_started' | 'in_progress' | 'completed' | 'rejected';
  category?: string;
  dueDate?: any;
  attachments?: TaskAttachment[];
  createdAt?: any;
  updatedAt?: any;
}

interface DocumentWithTask {
  attachment: TaskAttachment;
  taskId: string;
  taskTitle: string;
}

type SortOption = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc' | 'size-desc' | 'size-asc' | 'task-asc' | 'task-desc';

const Documents: React.FC = () => {
  const { user, role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<DocumentWithTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');
  const [filterByTask, setFilterByTask] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [loadingTask, setLoadingTask] = useState(false);

  const loadDocuments = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const tasksRef = collection(db, 'tasks');
      const tasksQuery = query(
        tasksRef, 
        where('clientId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(tasksQuery);
      
      const allDocuments: DocumentWithTask[] = [];
      
      snapshot.forEach((doc) => {
        const taskData = doc.data() as Task;
        const taskId = doc.id;
        const taskTitle = taskData.title || 'Untitled Task';
        
        // Extract all attachments from this task
        if (taskData.attachments && taskData.attachments.length > 0) {
          taskData.attachments.forEach((attachment) => {
            allDocuments.push({
              attachment,
              taskId,
              taskTitle
            });
          });
        }
      });
      
      // Initial sort by upload date (newest first) - will be re-sorted based on sortBy state
      allDocuments.sort((a, b) => {
        const aDate = a.attachment.uploadedAt?.toDate 
          ? a.attachment.uploadedAt.toDate() 
          : (a.attachment.uploadedAt ? new Date(a.attachment.uploadedAt) : new Date(0));
        const bDate = b.attachment.uploadedAt?.toDate 
          ? b.attachment.uploadedAt.toDate() 
          : (b.attachment.uploadedAt ? new Date(b.attachment.uploadedAt) : new Date(0));
        return bDate.getTime() - aDate.getTime();
      });
      
      setDocuments(allDocuments);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      setError('Failed to load documents: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!roleLoading && role !== 'client') {
      navigate('/client-dashboard');
    }
  }, [roleLoading, role, navigate]);

  useEffect(() => {
    if (role === 'client' && user) {
      loadDocuments();
    }
  }, [role, user, loadDocuments]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date: any): string => {
    if (!date) return 'Unknown date';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    if (isNaN(dateObj.getTime())) return 'Unknown date';
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTaskClick = async (taskId: string) => {
    try {
      setLoadingTask(true);
      const taskDocRef = doc(db, 'tasks', taskId);
      const taskDocSnap = await getDoc(taskDocRef);
      
      if (taskDocSnap.exists()) {
        const taskData = taskDocSnap.data() as Task;
        setSelectedTask({
          ...taskData,
          id: taskDocSnap.id
        });
      } else {
        setError('Task not found');
        setLoadingTask(false);
      }
    } catch (error: any) {
      console.error('Error loading task:', error);
      setError('Failed to load task: ' + error.message);
      setLoadingTask(false);
    } finally {
      setLoadingTask(false);
    }
  };

  // Get unique task titles for filter dropdown
  const uniqueTaskTitles = Array.from(new Set(documents.map(doc => doc.taskTitle))).sort();

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, filterByTask]);

  // Sort and filter documents
  const sortedAndFilteredDocuments = React.useMemo(() => {
    let filtered = documents;
    
    // Apply task filter
    if (filterByTask !== 'all') {
      filtered = documents.filter(doc => doc.taskTitle === filterByTask);
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          const aDateDesc = a.attachment.uploadedAt?.toDate 
            ? a.attachment.uploadedAt.toDate() 
            : (a.attachment.uploadedAt ? new Date(a.attachment.uploadedAt) : new Date(0));
          const bDateDesc = b.attachment.uploadedAt?.toDate 
            ? b.attachment.uploadedAt.toDate() 
            : (b.attachment.uploadedAt ? new Date(b.attachment.uploadedAt) : new Date(0));
          return bDateDesc.getTime() - aDateDesc.getTime();
        
        case 'date-asc':
          const aDateAsc = a.attachment.uploadedAt?.toDate 
            ? a.attachment.uploadedAt.toDate() 
            : (a.attachment.uploadedAt ? new Date(a.attachment.uploadedAt) : new Date(0));
          const bDateAsc = b.attachment.uploadedAt?.toDate 
            ? b.attachment.uploadedAt.toDate() 
            : (b.attachment.uploadedAt ? new Date(b.attachment.uploadedAt) : new Date(0));
          return aDateAsc.getTime() - bDateAsc.getTime();
        
        case 'name-asc':
          return a.attachment.fileName.localeCompare(b.attachment.fileName);
        
        case 'name-desc':
          return b.attachment.fileName.localeCompare(a.attachment.fileName);
        
        case 'size-desc':
          return b.attachment.fileSize - a.attachment.fileSize;
        
        case 'size-asc':
          return a.attachment.fileSize - b.attachment.fileSize;
        
        case 'task-asc':
          return a.taskTitle.localeCompare(b.taskTitle);
        
        case 'task-desc':
          return b.taskTitle.localeCompare(a.taskTitle);
        
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [documents, sortBy, filterByTask]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedAndFilteredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDocuments = sortedAndFilteredDocuments.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (roleLoading || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (role !== 'client') {
    return null;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1>Documents</h1>
          <button
            onClick={() => navigate('/client-dashboard')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Sort and Filter Controls */}
        {documents.length > 0 && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1.5rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#495057' }}>
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                <option value="date-desc">Date (Newest First)</option>
                <option value="date-asc">Date (Oldest First)</option>
                <option value="name-asc">File Name (A-Z)</option>
                <option value="name-desc">File Name (Z-A)</option>
                <option value="size-desc">File Size (Largest First)</option>
                <option value="size-asc">File Size (Smallest First)</option>
                <option value="task-asc">Task Name (A-Z)</option>
                <option value="task-desc">Task Name (Z-A)</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#495057' }}>
                Filter by Task
              </label>
              <select
                value={filterByTask}
                onChange={(e) => setFilterByTask(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                <option value="all">All Tasks</option>
                {uniqueTaskTitles.map((taskTitle) => (
                  <option key={taskTitle} value={taskTitle}>
                    {taskTitle}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {documents.length === 0 && !loading && (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            color: '#6c757d'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
            <h3 style={{ marginBottom: '0.5rem', color: '#495057' }}>No documents found</h3>
            <p>Documents attached to your tasks will appear here.</p>
          </div>
        )}

        {paginatedDocuments.length > 0 && (
          <>
            <div style={{
              display: 'grid',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {paginatedDocuments.map((docWithTask) => {
              const { attachment, taskTitle } = docWithTask;
              return (
                <div
                  key={attachment.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1.5rem',
                    backgroundColor: '#ffffff',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '1rem',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{ fontSize: '2rem' }}>📎</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ 
                          margin: 0, 
                          marginBottom: '0.25rem',
                          color: '#212529',
                          fontSize: '1.1rem',
                          wordBreak: 'break-word'
                        }}>
                          {attachment.fileName}
                        </h3>
                        <div style={{ 
                          color: '#6c757d', 
                          fontSize: '0.875rem',
                          marginBottom: '0.5rem'
                        }}>
                          <strong>Task:</strong>{' '}
                          <button
                            onClick={() => handleTaskClick(docWithTask.taskId)}
                            style={{
                              color: '#007bff',
                              textDecoration: 'none',
                              fontWeight: '500',
                              cursor: 'pointer',
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              font: 'inherit',
                              textAlign: 'left'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = 'none';
                            }}
                          >
                            {taskTitle}
                          </button>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          gap: '1rem',
                          flexWrap: 'wrap',
                          color: '#6c757d',
                          fontSize: '0.875rem'
                        }}>
                          <span><strong>Size:</strong> {formatFileSize(attachment.fileSize)}</span>
                          <span><strong>Uploaded:</strong> {formatDate(attachment.uploadedAt)}</span>
                          {attachment.uploadedByName && (
                            <span><strong>By:</strong> {attachment.uploadedByName}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginLeft: '1rem' }}>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={async () => {
                        // Log document access
                        if (user && user.email && role) {
                          logDataAccess(
                            user.uid,
                            user.email,
                            role as 'admin' | 'staff' | 'client',
                            'document',
                            attachment.id,
                            `Document accessed: ${attachment.fileName} (Task: ${taskTitle})`,
                            false
                          ).catch((err: any) => console.error('Failed to log document access:', err));
                        }
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontWeight: '500',
                        display: 'inline-block',
                        transition: 'background-color 0.2s ease',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0056b3';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#007bff';
                      }}
                    >
                      View Document
                    </a>
                  </div>
                </div>
              );
            })}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '2rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: currentPage === 1 ? '#e9ecef' : '#007bff',
                    color: currentPage === 1 ? '#6c757d' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: currentPage === page ? '#007bff' : 'white',
                          color: currentPage === page ? 'white' : '#007bff',
                          border: '1px solid #007bff',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          minWidth: '2.5rem'
                        }}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span key={page} style={{ padding: '0 0.25rem', color: '#6c757d' }}>
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: currentPage === totalPages ? '#e9ecef' : '#007bff',
                    color: currentPage === totalPages ? '#6c757d' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Next
                </button>
              </div>
            )}

            {/* Pagination Info */}
            {sortedAndFilteredDocuments.length > 0 && (
              <div style={{
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '0.875rem',
                marginTop: '1rem'
              }}>
                Showing {startIndex + 1} to {Math.min(endIndex, sortedAndFilteredDocuments.length)} of {sortedAndFilteredDocuments.length} documents
              </div>
            )}
          </>
        )}

        {documents.length > 0 && sortedAndFilteredDocuments.length === 0 && (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            color: '#6c757d'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
            <h3 style={{ marginBottom: '0.5rem', color: '#495057' }}>No documents match your filter</h3>
            <p>Try adjusting your filter criteria to see more documents.</p>
          </div>
        )}
      </div>

      {/* Task Details Modal */}
      {selectedTask && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '2rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedTask(null);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              padding: '2rem',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {loadingTask ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Loading task details...</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h2 style={{ margin: 0 }}>Task Details</h2>
                  <button
                    onClick={() => setSelectedTask(null)}
                    style={{
                      padding: '0.5rem',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                      lineHeight: '1'
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Task Information</h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Title</div>
                    <div style={{ fontSize: '1rem', fontWeight: '500' }}>{selectedTask.title}</div>
                  </div>
                  {selectedTask.description && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Description</div>
                      <div style={{ fontSize: '1rem', whiteSpace: 'pre-wrap' }}>{selectedTask.description}</div>
                    </div>
                  )}
                  {selectedTask.priority && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Priority</div>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        backgroundColor: 
                          selectedTask.priority === 'urgent' ? '#dc3545' :
                          selectedTask.priority === 'high' ? '#fd7e14' :
                          selectedTask.priority === 'medium' ? '#ffc107' : '#28a745',
                        color: selectedTask.priority === 'medium' ? '#000' : '#fff',
                        display: 'inline-block'
                      }}>
                        {selectedTask.priority}
                      </span>
                    </div>
                  )}
                  {selectedTask.status && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Status</div>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        backgroundColor: 
                          selectedTask.status === 'completed' ? '#28a745' :
                          selectedTask.status === 'in_progress' ? '#ffc107' :
                          selectedTask.status === 'rejected' ? '#dc3545' : '#6c757d',
                        color: selectedTask.status === 'in_progress' ? '#000' : '#fff',
                        display: 'inline-block'
                      }}>
                        {selectedTask.status.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  {selectedTask.category && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Category</div>
                      <div style={{ fontSize: '1rem' }}>{selectedTask.category}</div>
                    </div>
                  )}
                  {selectedTask.dueDate && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Due Date</div>
                      <div style={{ fontSize: '1rem' }}>
                        {selectedTask.dueDate?.toDate ? selectedTask.dueDate.toDate().toLocaleDateString() : 'No due date'}
                      </div>
                    </div>
                  )}
                  {selectedTask.createdAt && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Created</div>
                      <div style={{ fontSize: '1rem' }}>
                        {selectedTask.createdAt?.toDate ? selectedTask.createdAt.toDate().toLocaleString() : 'N/A'}
                      </div>
                    </div>
                  )}
                </div>

                {/* Attachments Section */}
                {selectedTask.attachments && selectedTask.attachments.length > 0 && (
                  <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Attachments</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {selectedTask.attachments.map((attachment) => (
                        <div key={attachment.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '4px',
                          border: '1px solid #dee2e6'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{attachment.fileName}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                              {formatFileSize(attachment.fileSize)}
                              {attachment.uploadedByName && ` • Uploaded by ${attachment.uploadedByName}`}
                              {attachment.uploadedAt && (
                                ` • ${formatDate(attachment.uploadedAt)}`
                              )}
                            </div>
                          </div>
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#007bff',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              marginLeft: '1rem'
                            }}
                          >
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6', textAlign: 'center' }}>
                  <button
                    onClick={() => navigate('/client-tasks')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500'
                    }}
                  >
                    View Full Task Page
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;

